import React, { useEffect, useState } from 'react';
import { TableCell, TableRow } from 'src/components/ui/table';
import { clsx } from 'clsx';
import { Switch } from 'src/components/ui/switch';
import { EditWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/EditWorkflowPopup/EditWorkflowPopup';
import { DeleteWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/DeleteWorkflowPopup/DeleteWorkflowPopup';
import { RowActionDescription } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/RowActionDescription/RowActionDescription';
import { WorkflowsChevronIcon } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsRow/WorkflowsRowIcons';

export const WorkflowsRow = ({
  index = 0,
  row = {} as Record<string, any>,
  readonly = true,
  isCustom = true,
  compact = true,
  onWorkflowChange = (() => {}) as (workflow: any, index: any) => void,
  onWorkflowDelete = (() => {}) as (index: any) => void,
  rowClassName = '',
}: {
  index?: number;
  row?: Record<string, any>;
  readonly?: boolean;
  isCustom?: boolean;
  compact?: boolean;
  onWorkflowChange?: (workflow: any, index: any) => void;
  onWorkflowDelete?: (index: any) => void;
  rowClassName?: string;
  [key: string]: any;
}) => {
  const [workflow, setWorkflow] = useState({ ...row });
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setWorkflow({ ...row });
  }, [readonly, row]);

  const onChange = (newWorkflow) => {
    onWorkflowChange(newWorkflow, index);
  };

  const onDelete = () => {
    onWorkflowDelete(index);
  };

  const descriptionContent = (
    <>
      <RowActionDescription
        first={true}
        isAction={false}
        workflow={workflow}
        action={workflow.action}
      />
      {Array.isArray(workflow.action) ? (
        workflow.action.map((action, idx, array) => (
          <RowActionDescription
            first={false}
            last={idx === array.length - 1}
            key={`${action.type}-${idx}`}
            isAction={true}
            workflow={workflow}
            action={action}
          />
        ))
      ) : (
        <RowActionDescription
          last={true}
          isAction={true}
          workflow={workflow}
          action={workflow.action}
        />
      )}
    </>
  );

  return (
    <TableRow className={clsx(!readonly && 'group', expanded && 'bg-light', rowClassName)}>
      <TableCell
        colSpan={2}
        padding={'px-5 py-2.5'}
        className={'!rounded-none !border-l-0 !border-r-0 border-b-light align-top'}
      >
        <div className={'flex w-full flex-col gap-2.5'}>
          <div
            className={'flex w-full flex-row items-center justify-between gap-4 cursor-pointer'}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <button
              type={'button'}
              className={'flex min-w-0 flex-row items-center gap-2'}
              title={workflow.name}
            >
              <span
                className={clsx(
                  'inline-block overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-ebony',
                  compact ? 'max-w-[350px]' : 'max-w-[200px]',
                )}
              >
                {workflow.name}
              </span>
              <WorkflowsChevronIcon expanded={expanded} />
            </button>
            <div className={'flex shrink-0 flex-row items-center gap-5'}>
              {!readonly && isCustom && <DeleteWorkflowPopup row={row} onSubmit={onDelete} />}
              {!readonly && <EditWorkflowPopup isCustom={isCustom} row={row} onSubmit={onChange} />}
              <Switch
                defaultChecked={workflow.enabled}
                checked={workflow.enabled}
                onCheckedChange={(enabled) => onChange({ ...workflow, enabled })}
                disabled={readonly || !isCustom}
              />
            </div>
          </div>
          {expanded && (
            <div className={'flex w-full flex-col items-start gap-1'}>{descriptionContent}</div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
